import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { invoiceNumberShort, hasDisplayNumber } from '../utils/invoiceDisplay';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Bidder-facing invoice list (Feature 1). Read-only. Shows only the signed-in
 * user's invoices by querying `where('uid','==', currentUser.uid)` — which is
 * exactly what the owner-scoped Firestore read rule permits. Invoices written
 * before the F0 uid backfill (no `uid` field) will not appear here.
 */

interface BidderInvoice {
  /** DOC ID, not a label — used for the row key and the /my-invoices/:n link. */
  invoiceNumber: string;
  /** The human, buyer-facing number (8001…). Absent until backfilled. */
  displayInvoiceNumber?: number;
  auctionTitle: string;
  items?: Array<{ lotNumber: string; title: string; highBid: number }>;
  total?: number;
  status?: 'unpaid' | 'paid' | 'partial' | 'picked_up';
  paidAt?: string;
  createdAt?: string;
}

const MyInvoices: React.FC = () => {
  const { currentUser } = useAuth();
  const [invoices, setInvoices] = useState<BidderInvoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      try {
        const q = query(collection(db, 'invoices'), where('uid', '==', currentUser.uid));
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => d.data() as BidderInvoice);
        rows.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        if (active) setInvoices(rows);
      } catch (e) {
        console.error('Failed to load your invoices:', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [currentUser]);

  if (loading) {
    return <div className="max-w-4xl mx-auto p-6 text-center text-gray-500">Loading your invoices…</div>;
  }

  const isPaid = (inv: BidderInvoice) => inv.status === 'paid' || !!inv.paidAt;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold">My Invoices</h1>

      {invoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          You don't have any invoices yet. Invoices appear here after a sale you won closes.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((inv) => (
                <tr key={inv.invoiceNumber} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={hasDisplayNumber(inv)
                      ? 'font-semibold text-gray-900'
                      : 'font-mono text-xs text-gray-500 break-all'}>
                      {invoiceNumberShort(inv)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{inv.auctionTitle}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{inv.items?.length ?? 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-green-600">
                    ${(inv.total ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span
                      className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                        isPaid(inv) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {isPaid(inv) ? 'Paid' : 'Unpaid'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                    <Link to={`/my-invoices/${inv.invoiceNumber}`} className="text-blue-600 hover:text-blue-900 font-medium">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MyInvoices;
